import { startGame } from '../commands/casinoCommands';
import {
  CASINO_CATEGORY_ID_NAME_MAP,
  CASINO_CATEGORY_PATH_NAME_MAP,
  CASINO_FILTERS_FOR_REORDERING,
  CASINO_GAME_TAGS,
  MAX_NUMBER_OF_CASINO_TAGS_TO_DISPLAY,
  NETENT_LIBRARY_SRC_PROD,
  NETENT_LIBRARY_SRC_TEST,
  NETENT_STATIC_SERVER_URL_PROD,
  NETENT_STATIC_SERVER_URL_TEST,
  NETNET_GAME_SERVER_URL_PROD,
  NETNET_GAME_SERVER_URL_TEST,
} from '../data-types/casino/casinoConstants';
import { CASINO_CATEGORY_IDS, CASINO_CATEGORY_PATH_NAMES, CASINO_TAG_ID } from '../data-types/casino/casinoEnums';
import { Filter, Game, GameStartAttributeLaunch, NetentProviderConfig } from '../data-types/casino/casinoInterfaces';
import { casinoPromotionTypes } from '../data-types/promotions/promotionsConstants';
import { SEARCH_PARAM_KEY } from '../data-types/url/urlEnums';
import { PROJECT_PHOENIX_DOMAIN_ORIGIN } from '../initialisation/initialisation';
import { isBetkwiffDomain, isProductionBetkwiffKenyaDomain, isProjectPhoenix } from '../international/internationalUtils';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { setCasinoNetPosition } from '../state/actions/casinoActions';
import { setCasinoGameTagDialogData } from '../state/actions/dialogActions';
import { store } from '../state/store';
import { isProductionEnv } from './general';
import { enqueueSnackbarPrompt } from './prompts';

export const startCasinoGame = async (game: number) => {
  try {
    return await startGame(game);
  } catch (e) {
    enqueueSnackbarPrompt(e.message, { closeOnClick: true });
    return undefined;
  }
};

export const getNetentProviderConfig = () => {
  if (isProductionEnv()) {
    return {
      libraryURL: NETENT_LIBRARY_SRC_PROD,
      gameServerURL: NETNET_GAME_SERVER_URL_PROD,
      staticServerURL: NETENT_STATIC_SERVER_URL_PROD,
    };
  }
  return {
    libraryURL: NETENT_LIBRARY_SRC_TEST,
    staticServerURL: NETENT_STATIC_SERVER_URL_TEST,
    gameServerURL: NETNET_GAME_SERVER_URL_TEST,
  };
};

export const getNetentGameConfig = (
  netentConfig: NetentProviderConfig,
  attributeLaunch: GameStartAttributeLaunch,
  sanitizedLobbyLink: string,
) => ({
  gameId: attributeLaunch.gameId,
  staticServerURL: netentConfig.staticServerURL,
  gameServerURL: netentConfig.gameServerURL,
  sessionId: attributeLaunch.sessionId,
  lobbyURL: sanitizedLobbyLink,
  targetElement: 'game-area',
  launchType: attributeLaunch.launchType,
  walletMode: attributeLaunch.walletMode,
  casinoBrand: attributeLaunch.casinoBrand,
  iframeSandbox:
    'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation allow-same-origin allow-forms allow-pointer-lock',
});

export const handleNetentError = (error: Error) => {
  throw error;
};

export const resetCasinoNetPosition = () => {
  const {
    casino: { casinoNetPosition },
  } = store.getState();

  if (casinoNetPosition !== 0) {
    setCasinoNetPosition(0);
  }
};

export const getSanitizedGameUrl = (url: string) => {
  if (url && isProjectPhoenix()) {
    return url.replace('kwiff.com/casino-game-closed', `${PROJECT_PHOENIX_DOMAIN_ORIGIN}.com/casino-game-closed`);
  }

  if (url && isProductionBetkwiffKenyaDomain()) {
    return url.replace('kwiff.com/casino-game-closed', 'betkwiff.ke/casino-game-closed');
  }

  if (url && isBetkwiffDomain()) {
    return url.replace('kwiff.com/casino-game-closed', 'betkwiff.com/casino-game-closed');
  }

  return url;
};

export const isOnAviatorPage = () => window.location.pathname.startsWith(PATHS.APP.AVIATOR);

export const isOnCasinoGamePage = () => window.location.pathname.startsWith(PATHS.APP.CASINO.GAME);

export const getCasinoPathNameByCategory = (category: number | string) => {
  const mapItem = CASINO_CATEGORY_PATH_NAME_MAP.find(item => item.pathName === category || item.categoryId === Number(category));
  return mapItem ? mapItem.pathName : CASINO_CATEGORY_PATH_NAMES.FEATURED;
};

export const getCasinoCategoryIdByPathName = (categoryPath: string) => {
  const mapItem = CASINO_CATEGORY_PATH_NAME_MAP.find(item => item.pathName === categoryPath || item.categoryId === Number(categoryPath));
  return mapItem ? mapItem.categoryId : CASINO_CATEGORY_IDS.FEATURED;
};

export const getCasinoFilterPathName = (filterName: string) =>
  filterName
    .replaceAll(/[\s]/g, '-')
    .replaceAll(/[!]/g, '')
    .replaceAll(/@/g, '-at-')
    .replaceAll(/--at--/g, '-at-')
    .replaceAll(/&/g, 'and')
    .toLowerCase()
    .replaceAll(/it's/g, 'it-is');

export const getRawCasinoFilterName = (filterName: string | undefined) => filterName && getCasinoFilterPathName(filterName);

export const areCasinoCategoriesEquivalent = (category1: string | undefined, category2: string | undefined) => {
  if (category1 === category2) {
    return true;
  }

  const categoryNumber1 = Number(category1);
  const categoryNumber2 = Number(category2);

  if (!isNaN(categoryNumber1)) {
    const mapItem = CASINO_CATEGORY_PATH_NAME_MAP.find(item => item.categoryId === categoryNumber1);
    if (mapItem && mapItem.pathName === category2) {
      return true;
    }
  }

  if (!isNaN(categoryNumber2)) {
    const mapItem = CASINO_CATEGORY_PATH_NAME_MAP.find(item => item.categoryId === categoryNumber2);
    if (mapItem && mapItem.pathName === category1) {
      return true;
    }
  }

  return false;
};

export const areCasinoFiltersEquivalent = (filter1: string | undefined, filter2: string | undefined) =>
  getRawCasinoFilterName(filter1) === getRawCasinoFilterName(filter2);

export const isLiveCasinoCategory = (category: string) =>
  Number(category) === CASINO_CATEGORY_IDS.LIVE_CASINO || category === CASINO_CATEGORY_PATH_NAMES.LIVE_CASINO;

export const replaceCasinoPathsIfNeeded = (
  category: string,
  categoryPathName: string,
  filter: string | undefined,
  filterPathName: string | undefined,
) => {
  if (category !== categoryPathName) {
    if (filterPathName) {
      browserHistory.replace(PATHS.APP.CASINO.FILTER(categoryPathName, filterPathName));
    } else {
      browserHistory.replace(PATHS.APP.CASINO.CATEGORY(categoryPathName));
    }
  } else {
    if (filter !== filterPathName && filterPathName) {
      browserHistory.replace(PATHS.APP.CASINO.FILTER(categoryPathName, filterPathName));
    }
  }
};

export const getCasinoCategoryNameById = (casinoCategory: number) => {
  const foundItem = CASINO_CATEGORY_ID_NAME_MAP.find(item => item.id === casinoCategory);
  return foundItem ? foundItem.name : '';
};

export const getIconSourceByCasinoTagId = (tagId: CASINO_TAG_ID) => {
  const tagDisplayInfo = CASINO_GAME_TAGS.find(tag => tag.id === tagId);

  return (tagDisplayInfo && tagDisplayInfo.icon) || '';
};

export const handleTagIconClick = (tagId: number, game: Game) => () => setCasinoGameTagDialogData({ tagId, game, open: true });

export const getGameTagsToDisplay = (game: Game) =>
  (game.tagIds && game.tagIds.slice(0, MAX_NUMBER_OF_CASINO_TAGS_TO_DISPLAY)) || ([] as CASINO_TAG_ID[]);

export const getCasinoPathFromNativeAppRedirect = (queryParams: URLSearchParams) => {
  const gameId = queryParams.get(SEARCH_PARAM_KEY.GAME_ID) || '';
  const providerId = queryParams.get(SEARCH_PARAM_KEY.PROVIDER_ID) || '';
  const categoryId = queryParams.get(SEARCH_PARAM_KEY.CATEGORY_ID) || '';
  const filterId = queryParams.get(SEARCH_PARAM_KEY.FILTER_ID) || '';
  const casinoPromotionType = queryParams.get(SEARCH_PARAM_KEY.CASINO_PROMOTION_TYPE) || '';

  switch (+casinoPromotionType) {
    case casinoPromotionTypes.game:
      return PATHS.APP.CASINO.GAME_WITH_PARAMS(gameId, providerId);
    case casinoPromotionTypes.category:
      const categoryPathName = getCasinoPathNameByCategory(categoryId);
      return PATHS.APP.CASINO.CATEGORY(categoryPathName);
    case casinoPromotionTypes.filter:
      const categoryPathNameForFilter = getCasinoPathNameByCategory(categoryId);
      const filterPathName = filterId && getCasinoFilterPathName(filterId);
      return PATHS.APP.CASINO.FILTER(categoryPathNameForFilter, filterPathName);
    default:
      return PATHS.APP.CASINO.ROOT;
  }
};

export const getCasinoPathFromDeepLinkRedirect = (queryParams: URLSearchParams) => {
  const casinoPromotionType = queryParams.get(SEARCH_PARAM_KEY.CASINO_PROMOTION_TYPE) || '';
  const gameId = queryParams.get(SEARCH_PARAM_KEY.GAME_ID) || '';
  const providerId = queryParams.get(SEARCH_PARAM_KEY.PROVIDER_ID) || '';
  const categoryCasino = queryParams.get(SEARCH_PARAM_KEY.CATEGORY_CASINO) || '';
  const filterCasino = queryParams.get(SEARCH_PARAM_KEY.FILTER_CASINO) || '';

  switch (+casinoPromotionType) {
    case casinoPromotionTypes.game: {
      return gameId && providerId ? PATHS.APP.CASINO.GAME_WITH_PARAMS(gameId, providerId) : PATHS.APP.CASINO.ROOT;
    }
    case casinoPromotionTypes.category: {
      if (categoryCasino) {
        const categoryPathName = getCasinoPathNameByCategory(categoryCasino);
        return PATHS.APP.CASINO.CATEGORY(categoryPathName);
      }
      return PATHS.APP.CASINO.ROOT;
    }
    case casinoPromotionTypes.filter: {
      if (categoryCasino && filterCasino) {
        const categoryPathName = getCasinoPathNameByCategory(categoryCasino);
        const filterPathName = getCasinoFilterPathName(filterCasino);
        return PATHS.APP.CASINO.FILTER(categoryPathName, filterPathName);
      }
      return PATHS.APP.CASINO.ROOT;
    }
    default:
      return PATHS.APP.CASINO.ROOT;
  }
};

export const filterMatcher = (searchedId: number | undefined, searchedName: string) => (filter: Filter) =>
  (searchedId && filter.id === searchedId) || filter.name === searchedName;

export const getReorderedCasinoFilters = (filters: Filter[]) => {
  const filtersPart1 = [] as Filter[];
  let filtersPart2 = [] as Filter[];
  const filtersCopy = [...filters];

  const kwiffPicksMatcher = filterMatcher(CASINO_FILTERS_FOR_REORDERING.KWIFF_PICKS.id, CASINO_FILTERS_FOR_REORDERING.KWIFF_PICKS.name);
  const kwiffPicksIndex = filtersCopy.findIndex(kwiffPicksMatcher);

  if (kwiffPicksIndex !== -1) {
    filtersPart1.push(filtersCopy[kwiffPicksIndex]);
    filtersCopy.splice(kwiffPicksIndex, 1);
  }

  const mostPopularMatcher = filterMatcher(CASINO_FILTERS_FOR_REORDERING.MOST_POPULAR.id, CASINO_FILTERS_FOR_REORDERING.MOST_POPULAR.name);
  const mostPopularIndex = filtersCopy.findIndex(mostPopularMatcher);

  if (mostPopularIndex !== -1) {
    filtersPart1.push(filtersCopy[mostPopularIndex]);
    filtersCopy.splice(mostPopularIndex, 1);
  }

  const newGamesMatcher = filterMatcher(CASINO_FILTERS_FOR_REORDERING.NEW_GAMES.id, CASINO_FILTERS_FOR_REORDERING.NEW_GAMES.name);
  const newGamesIndex = filtersCopy.findIndex(newGamesMatcher);

  if (newGamesIndex !== -1) {
    filtersPart2.push(filtersCopy[newGamesIndex]);
    filtersCopy.splice(newGamesIndex, 1);
  }

  const recentlyPlayedMatcher = filterMatcher(
    CASINO_FILTERS_FOR_REORDERING.RECENTLY_PLAYED.id,
    CASINO_FILTERS_FOR_REORDERING.RECENTLY_PLAYED.name,
  );
  const recentlyPlayedIndex = filtersCopy.findIndex(recentlyPlayedMatcher);

  if (recentlyPlayedIndex !== -1) {
    filtersPart2.push(filtersCopy[recentlyPlayedIndex]);
    filtersCopy.splice(recentlyPlayedIndex, 1);
  }

  filtersPart2 = [...filtersPart2, ...filtersCopy];

  return { filtersPart1, filtersPart2 };
};
